<template>
  <div class="py-4 container-fluid">
    <div class="row min-vh-80">
      <div class="col-lg-8 col-md-10 col-12 m-auto">
        <h3 class="mt-3 mb-3 text-center">Files Received</h3>
        <p class="lead font-weight-normal opacity-8 mb-7 text-center">
          You received a file from <span class="font-weight-bold">{{ ownerName }}</span>. 
          <br /> Click Download to view it.
        </p>
        
        <!-- Loading State -->
        <div v-if="isLoadingSharedFiles" class="text-center">
          <p>Loading file details...</p>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="text-center text-danger">
          <p>{{ error }}</p>
        </div>
        
        <!-- File Details -->
        <div v-else-if="file" class="card">
          <div class="card-body d-flex" style="justify-content: space-between;">
            <div class="icon-lg icon-shape d-flex justify-content-center file-icon-container">
              <i :class="`${icon}`" :style="{ color: color }"></i>
            </div>
            <div class="text-end pt-1 mt-4">
              <p class="text-sm mb-0 text-capitalize">{{ file.title }}</p>
              <p class="text-xs mb-0">Upload Date: {{ file.datetime }}</p>
              <p class="text-xs mb-0">Size: {{ file.size }}</p>
            </div>
          </div>
          <hr class="dark horizontal my-0" />
          <div class="card-footer p-3 m-0">
            <div class="text-end">
              <!-- Download Progress -->
              <div v-if="showProgress" class="mb-2">
                <material-progress color="success" :percentage="progressPercentage" />
                <div v-if="downloadStatus" class="text-xs text-muted mt-1">
                  {{ downloadStatus }}
                </div>
              </div>
              
              <material-button
                type="button"
                color="shareguard"
                class="mb-0 ms-auto js-btn-next"
                :disabled="isDownloading"
                @click="downloadClick"
              >
                {{ isDownloading ? 'Downloading...' : 'Download' }}
              </material-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { determineFileType, determineFileColor, determineFileIcon } from '@/utils/files';
import MaterialButton from "@/components/ui/MaterialButton.vue";
import MaterialProgress from "@/components/ui/MaterialProgress.vue";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "ReceivedFileView",
  components: {
    MaterialButton,
    MaterialProgress
  },
  
  data() {
    return {
      showProgress: false,
      progressPercentage: 0,
      downloadStatus: '',
      isDownloading: false
    };
  },

  computed: {
    ...mapGetters("files", [
      "sharedFiles",
      "isLoadingSharedFiles"
    ]),
    ...mapState("contacts", ["contactList"]),
    
    file() {
      const fileId = this.$route.params.id;
      return this.sharedFiles.find(f => f.id === fileId);
    },
    
    error() {
      const fileId = this.$route.params.id;
      if (!this.isLoadingSharedFiles && !this.file) {
        return `File with ID ${fileId} not found`;
      }
      return null;
    },
    
    icon() {
      if (!this.file) return '';
      return determineFileIcon(determineFileType(this.file.title || ''));
    },
    
    color() {
      if (!this.file) return '';
      return determineFileColor(determineFileType(this.file.title || ''));
    },
    
    ownerName() {
      if (!this.file?.owner) return 'Unknown';
      const contactFound = this.contactList.filter(contact => contact.email === this.file.owner);
      return contactFound && contactFound[0] ? contactFound[0].name : this.file.owner;
    },
    
    ownerColor() {
      if (!this.file?.owner) return null;
      const contactFound = this.contactList.filter(contact => contact.email === this.file.owner);
      return contactFound && contactFound[0] ? contactFound[0].color : null;
    }
  },

  async created() {
    await this.loadFileData();
  },
  
  methods: {
    ...mapActions("files", ["fetchSharedFilesAction", "downloadSharedFileAction"]),
    ...mapActions("snackbar", ["showSnackbar"]),
    
    async loadFileData() {
      try {
        await this.fetchSharedFilesAction();
      } catch (error) {
        console.error("Failed to load shared files:", error);
      }
    },

    downloadClick() {
      if (this.isDownloading) {
        return;
      }
      
      this.isDownloading = true;
      this.showProgress = true;
      this.progressPercentage = 0;
      this.downloadStatus = 'Starting download...';
      
      this.downloadSharedFileAction({
        fileId: this.file.id,
        onProgress: (progress, progressInfo) => {
          this.progressPercentage = Number(progress.toFixed(2));
          this.downloadStatus = progressInfo?.status || 'Downloading...';
        }
      }).then(() => {
        this.showProgress = false;
        this.progressPercentage = 0;
        this.downloadStatus = '';
        this.isDownloading = false;
      }).catch(() => {
        this.showProgress = false;
        this.progressPercentage = 0;
        this.downloadStatus = '';
        this.isDownloading = false;
        this.showSnackbar({ title: "Download failed", color: "danger" });
      });
    }
  }
};
</script>

<style scoped>
.file-meta {
  font-size: 12px;
  color: #666;
}

.icon-button {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: transform 0.2s;
}
.icon-button:hover {
  transform: scale(1.3);
}

.progress {
  height: 4px;
  margin-bottom: 4px;
}

.file-icon-container {
  min-width: 60px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-icon-container i {
  font-size: 2.5rem;
  transition: transform 0.2s ease;
}

/* Responsive sizing for different screen sizes */
@media (max-width: 768px) {
  .file-icon-container {
    min-width: 50px;
    min-height: 50px;
  }
  
  .file-icon-container i {
    font-size: 2rem;
  }
}

@media (max-width: 576px) {
  .file-icon-container {
    min-width: 45px;
    min-height: 45px;
  }
  
  .file-icon-container i {
    font-size: 1.75rem;
  }
}

@media (min-width: 992px) {
  .file-icon-container {
    min-width: 70px;
    min-height: 70px;
  }
  
  .file-icon-container i {
    font-size: 3rem;
  }
}
</style>
